<template>
  <v-dialog
    :model-value="modelValue"
    max-width="900px"
    persistent
    scrollable
    data-testid="tax-history-settings-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-primary text-white">
        <v-icon class="mr-2">mdi-cog</v-icon>
        {{ $t('taxHistorySettingsTitle') }}
      </v-card-title>

      <!-- Tabs sit outside v-card-text so the scrollable body cannot squeeze them; one tab drives every text field. -->
      <v-tabs v-model="langTab" color="primary" density="compact">
        <v-tab v-for="lang in langs" :key="lang.key" :value="lang.key">{{ lang.label }}</v-tab>
      </v-tabs>
      <v-divider />
      <v-progress-linear v-if="loading" indeterminate color="primary" height="3" />

      <v-card-text class="pt-4">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="comfortable"
          closable
          class="mb-4"
          data-testid="tax-history-settings-error"
          @click:close="error = ''"
        >
          {{ error }}
        </v-alert>

        <div class="form-section">
          <h4 class="section-title">{{ $t('taxHistoryStyleTitle') }}</h4>
          <v-row dense class="align-center">
            <v-col cols="12" md="5">
              <v-text-field
                v-model="form.consentLinkWording[langTab]"
                :label="`${$t('taxHistoryStyleWordingLabel')} (${activeLang.label})`"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-link-variant"
                counter="500"
                maxlength="500"
                :disabled="loading"
                data-testid="tax-history-style-wording"
              />
            </v-col>
            <v-col cols="8" md="4">
              <v-text-field
                v-model="form.consentLinkWordingColor"
                :label="$t('taxHistoryStyleColorLabel')"
                variant="outlined"
                density="comfortable"
                placeholder="#B3261E"
                persistent-placeholder
                :rules="[colorRule]"
                :disabled="loading"
                data-testid="tax-history-style-color"
              >
                <!-- Swatch doubles as the native picker and always shows the colour actually applied. -->
                <template #prepend-inner>
                  <div class="ths-swatch" :style="{ backgroundColor: safeColor }">
                    <input
                      type="color"
                      :value="safeColor"
                      class="ths-swatch-input"
                      :aria-label="$t('taxHistoryStyleColorLabel')"
                      @change="form.consentLinkWordingColor = $event.target.value.toUpperCase()"
                    />
                  </div>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="4" md="3" class="d-flex align-center">
              <v-switch
                v-model="form.consentLinkUnderline"
                :label="$t('taxHistoryStyleUnderlineLabel')"
                density="compact"
                color="primary"
                hide-details
                :disabled="loading"
                data-testid="tax-history-style-underline"
              />
            </v-col>
          </v-row>

          <div class="text-caption text-medium-emphasis">{{ $t('taxHistoryStylePreview') }}</div>
          <div class="ths-preview" data-testid="tax-history-style-preview">
            <span :style="linkStyle">{{ form.consentLinkWording[langTab] || '—' }}</span>
          </div>
        </div>

        <v-divider class="my-4" />

        <div class="form-section">
          <h4 class="section-title">{{ $t('taxHistoryButtonLabelsTitle') }}</h4>
          <div class="text-caption text-medium-emphasis mb-3">{{ $t('taxHistoryButtonLabelsHint') }}</div>
          <!-- Rows (state names + default texts) come from the API, never from a list kept here. -->
          <v-row dense>
            <v-col v-for="row in form.buttonLabels" :key="row.stateGroup" cols="12" md="6">
              <v-text-field
                v-model="row.label[langTab]"
                :label="row.stateName"
                :placeholder="row.defaultLabel[langTab]"
                persistent-placeholder
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-gesture-tap-button"
                counter="200"
                maxlength="200"
                :disabled="loading"
                :data-testid="`tax-history-button-label-${row.stateGroup}`"
              />
            </v-col>
          </v-row>
        </div>

        <v-divider class="my-4" />

        <div class="form-section">
          <h4 class="section-title">{{ $t('taxHistoryDateTemplatesTitle') }}</h4>
          <!-- The braces go in as a param: vue-i18n would read a raw brace pair in the message as interpolation. -->
          <div class="text-caption text-medium-emphasis">
            {{ $t('taxHistoryDateTemplatesHint', { example: '{d MMM yyyy}, {HH:mm}' }) }}
          </div>
          <div class="text-caption text-medium-emphasis mb-3">{{ $t('taxHistoryDateTemplatesSpecifiers') }}</div>
          <!-- Placeholders are the API's defaults; the preview is rendered by the API, so it is what the app will show. -->
          <v-row dense>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.createdDateTemplate[langTab]"
                :label="`${$t('taxHistoryDateTemplateCreatedLabel')} (${activeLang.label})`"
                :placeholder="form.defaultCreatedDateTemplate[langTab]"
                persistent-placeholder
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-clock"
                counter="200"
                maxlength="200"
                :disabled="loading"
                data-testid="tax-history-created-date-template"
              />
              <div class="text-caption text-medium-emphasis">{{ $t('taxHistoryDateTemplatePreview') }}</div>
              <div
                v-if="previewFailed"
                class="text-caption text-medium-emphasis"
                data-testid="tax-history-created-date-preview-failed"
              >
                {{ $t('taxHistoryDateTemplatePreviewFailed') }}
              </div>
              <div
                v-else
                class="ths-preview"
                :class="{ 'text-error': !!preview.createdError[langTab] }"
                data-testid="tax-history-created-date-preview"
              >
                {{ preview.createdError[langTab] || preview.createdText[langTab] || '—' }}
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.updatedDateTemplate[langTab]"
                :label="`${$t('taxHistoryDateTemplateUpdatedLabel')} (${activeLang.label})`"
                :placeholder="form.defaultUpdatedDateTemplate[langTab]"
                persistent-placeholder
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-clock"
                counter="200"
                maxlength="200"
                :disabled="loading"
                data-testid="tax-history-updated-date-template"
              />
              <div class="text-caption text-medium-emphasis">{{ $t('taxHistoryDateTemplatePreview') }}</div>
              <div
                v-if="previewFailed"
                class="text-caption text-medium-emphasis"
                data-testid="tax-history-updated-date-preview-failed"
              >
                {{ $t('taxHistoryDateTemplatePreviewFailed') }}
              </div>
              <div
                v-else
                class="ths-preview"
                :class="{ 'text-error': !!preview.updatedError[langTab] }"
                data-testid="tax-history-updated-date-preview"
              >
                {{ preview.updatedError[langTab] || preview.updatedText[langTab] || '—' }}
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          variant="outlined"
          :disabled="saving"
          data-testid="tax-history-settings-cancel"
          @click="close"
        >
          <v-icon start>mdi-close</v-icon>
          {{ $t('taxHistorySettingsCancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="saving"
          :disabled="loading || loadFailed"
          data-testid="tax-history-settings-save"
          @click="save"
        >
          <v-icon start>mdi-content-save</v-icon>
          {{ $t('taxHistoryStyleSave') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { taxHistoryV2Api } from '../../services/taxHistoryV2Api.js';

const DEFAULT_COLOR = '#B3261E';
const HEX = /^#[0-9a-f]{6}$/i;
const PREVIEW_DEBOUNCE_MS = 300;

// Wire shape of TaxHistoryLocalizedText — th / en / zh.
function localizedText(value, transform = (text) => text) {
  const source = value || {};
  return {
    th: transform(String(source.th || '')),
    en: transform(String(source.en || '')),
    zh: transform(String(source.zh || '')),
  };
}

function emptyForm() {
  return {
    consentLinkWording: localizedText(null),
    consentLinkWordingColor: DEFAULT_COLOR,
    consentLinkUnderline: true,
    buttonLabels: [],
    // Card date caption templates ('' = default) and the API's defaults, shown as placeholders.
    createdDateTemplate: localizedText(null),
    updatedDateTemplate: localizedText(null),
    defaultCreatedDateTemplate: localizedText(null),
    defaultUpdatedDateTemplate: localizedText(null),
  };
}

// Wire shape of TaxHistoryDateTemplatePreviewDto — an error '' means that language rendered fine.
function emptyPreview() {
  return {
    createdText: localizedText(null),
    createdError: localizedText(null),
    updatedText: localizedText(null),
    updatedError: localizedText(null),
  };
}

export default {
  name: 'TaxHistorySettingsDialog',
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      langs: [
        { key: 'th', label: 'ไทย (TH)' },
        { key: 'en', label: 'ENGLISH (EN)' },
        { key: 'zh', label: '中文 (ZH)' },
      ],
      langTab: 'th',
      form: emptyForm(),
      loading: false,
      loadFailed: false,
      saving: false,
      error: '',
      preview: emptyPreview(),
      previewTimer: null,
      previewSeq: 0,
      previewFailed: false,
    };
  },
  computed: {
    activeLang() {
      return this.langs.find((lang) => lang.key === this.langTab) || this.langs[0];
    },
    safeColor() {
      const raw = String(this.form.consentLinkWordingColor || '').trim();
      return HEX.test(raw) ? raw : DEFAULT_COLOR;
    },
    linkStyle() {
      return {
        color: this.safeColor,
        textDecoration: this.form.consentLinkUnderline ? 'underline' : 'none',
      };
    },
  },
  watch: {
    modelValue(open) {
      if (!open) return;
      this.langTab = 'th';
      this.form = emptyForm();
      this.error = '';
      clearTimeout(this.previewTimer);
      this.previewSeq += 1; // a reply still in flight from the last time the dialog was open is stale
      this.preview = emptyPreview();
      this.previewFailed = false;
      this.load();
    },
    // Only the templates are watched — switching the language tab needs no request (the reply has all 3 languages).
    'form.createdDateTemplate': { deep: true, handler: 'onDateTemplateChange' },
    'form.updatedDateTemplate': { deep: true, handler: 'onDateTemplateChange' },
  },
  beforeUnmount() {
    clearTimeout(this.previewTimer);
  },
  methods: {
    colorRule(value) {
      return HEX.test(String(value || '').trim()) || this.$t('taxHistoryStyleColorInvalid');
    },
    applyConfig(data) {
      const base = emptyForm();
      const rows = data && Array.isArray(data.buttonLabels) ? data.buttonLabels : [];
      this.form = {
        consentLinkWording: localizedText(data && data.consentLinkWording),
        consentLinkWordingColor: (data && data.consentLinkWordingColor) || base.consentLinkWordingColor,
        consentLinkUnderline:
          data && typeof data.consentLinkUnderline === 'boolean'
            ? data.consentLinkUnderline
            : base.consentLinkUnderline,
        buttonLabels: rows
          .filter((row) => row && row.stateGroup)
          .map((row) => ({
            stateGroup: row.stateGroup,
            stateName: row.stateName || row.stateGroup,
            label: localizedText(row.label),
            defaultLabel: localizedText(row.defaultLabel),
          })),
        createdDateTemplate: localizedText(data && data.createdDateTemplate),
        updatedDateTemplate: localizedText(data && data.updatedDateTemplate),
        defaultCreatedDateTemplate: localizedText(data && data.defaultCreatedDateTemplate),
        defaultUpdatedDateTemplate: localizedText(data && data.defaultUpdatedDateTemplate),
      };
    },
    async load() {
      this.loading = true;
      this.loadFailed = false;
      this.error = '';
      try {
        this.applyConfig(await taxHistoryV2Api.getStyleConfig());
        this.refreshPreview();
      } catch (e) {
        // Saving an unloaded form would overwrite the stored wording/labels with blanks.
        this.loadFailed = true;
        this.error = e.message;
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.error = '';
      const color = String(this.form.consentLinkWordingColor || '').trim();
      if (!HEX.test(color)) {
        this.error = this.$t('taxHistoryStyleColorInvalid');
        return;
      }
      const trim = (text) => text.trim();
      this.saving = true;
      try {
        const body = {
          consentLinkWording: localizedText(this.form.consentLinkWording, trim),
          consentLinkWordingColor: color,
          consentLinkUnderline: !!this.form.consentLinkUnderline,
          // null keeps the stored labels (e.g. the API returned no rows); a list is the full set.
          buttonLabels: this.form.buttonLabels.length
            ? this.form.buttonLabels.map((row) => ({
                stateGroup: row.stateGroup,
                label: localizedText(row.label, trim),
              }))
            : null,
          // Always the full set (blank = default): Save is only enabled after a successful load.
          createdDateTemplate: localizedText(this.form.createdDateTemplate, trim),
          updatedDateTemplate: localizedText(this.form.updatedDateTemplate, trim),
        };
        await taxHistoryV2Api.saveStyleConfig(body);
        this.$emit('saved', this.$t('taxHistorySettingsSaved'));
        this.close();
      } catch (e) {
        this.error = e.message;
      } finally {
        this.saving = false;
      }
    },
    onDateTemplateChange(value, previous) {
      // Typing edits the object in place (value === previous). Replacing the whole form (dialog open / load) is not an
      // edit — load() renders the loaded templates itself — so it is skipped to keep one request per change.
      if (value === previous) this.schedulePreview();
    },
    schedulePreview() {
      clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => {
        this.previewTimer = null;
        this.refreshPreview();
      }, PREVIEW_DEBOUNCE_MS);
    },
    async refreshPreview() {
      // Replies can land out of order while the admin types: only the newest request may paint.
      const seq = ++this.previewSeq;
      try {
        const data = await taxHistoryV2Api.previewDateTemplates({
          createdDateTemplate: localizedText(this.form.createdDateTemplate),
          updatedDateTemplate: localizedText(this.form.updatedDateTemplate),
        });
        if (seq !== this.previewSeq) return;
        this.preview = {
          createdText: localizedText(data && data.createdText),
          createdError: localizedText(data && data.createdError),
          updatedText: localizedText(data && data.updatedText),
          updatedError: localizedText(data && data.updatedError),
        };
        this.previewFailed = false;
      } catch {
        // The preview is a convenience: failing only swaps in a caption and never blocks Save.
        if (seq === this.previewSeq) this.previewFailed = true;
      }
    },
    close() {
      clearTimeout(this.previewTimer);
      this.$emit('update:modelValue', false);
    },
  },
};
</script>

<style scoped>
.form-section {
  margin-bottom: 24px;
}

.section-title {
  margin-bottom: 16px;
  font-weight: 600;
}

.ths-swatch {
  position: relative;
  width: 26px;
  height: 26px;
  margin: 0 8px 0 0;
  align-self: center;
  flex-shrink: 0;
  border-radius: 6px;
  border: 1.5px solid rgba(0, 0, 0, 0.12);
  cursor: pointer;
}

.ths-swatch-input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.ths-preview {
  margin-top: 4px;
  padding: 12px 16px;
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  text-align: center;
}
</style>
